/**
 * Drop-in Kompat-Shim für `react-router-dom`, wenn das Projekt komplett auf
 * TanStack Router (SSR) läuft. Sämtliche Komponenten und Hooks werden über
 * einen Vite-Alias hierher umgeleitet, damit die bestehenden Seiten und
 * Komponenten ohne Massen-Refactor weiterlaufen.
 */
import * as React from "react";
import { forwardRef } from "react";
import {
  Link as TSLink,
  Navigate as TSNavigate,
  useLocation as tsUseLocation,
  useNavigate as tsUseNavigate,
  useParams as tsUseParams,
  useRouterState,
} from "@tanstack/react-router";
import { cn } from "@/lib/utils";

type AnyProps = Record<string, unknown>;

export interface LinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  to: string;
  replace?: boolean;
  state?: unknown;
  preventScrollReset?: boolean;
  reloadDocument?: boolean;
  end?: boolean;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { to, replace, state: _state, preventScrollReset: _p, reloadDocument, ...rest },
  ref,
) {
  if (reloadDocument) {
    return <a ref={ref} href={to} {...rest} />;
  }
  return (
    // TanStack typisiert `to` strikt auf bekannte Routen. Wir übergeben
    // Runtime-Strings und casten deshalb bewusst über `unknown`.
    <TSLink
      ref={ref as never}
      {...(rest as AnyProps)}
      to={to as never}
      replace={replace}
    />
  );
});

export interface NavLinkRenderProps {
  isActive: boolean;
  isPending: boolean;
  isTransitioning: boolean;
}

export interface NavLinkProps
  extends Omit<LinkProps, "className" | "style" | "children"> {
  className?:
    | string
    | ((state: NavLinkRenderProps) => string | undefined);
  style?:
    | React.CSSProperties
    | ((state: NavLinkRenderProps) => React.CSSProperties | undefined);
  children?:
    | React.ReactNode
    | ((state: NavLinkRenderProps) => React.ReactNode);
  end?: boolean;
  caseSensitive?: boolean;
}

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(
  function NavLink({ to, className, style, children, end, ...rest }, ref) {
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    const isActive = end
      ? pathname === to
      : pathname === to || pathname.startsWith(`${to}/`);
    const state: NavLinkRenderProps = {
      isActive,
      isPending: false,
      isTransitioning: false,
    };
    const resolvedClass =
      typeof className === "function" ? className(state) : className;
    const resolvedStyle =
      typeof style === "function" ? style(state) : style;
    const resolvedChildren =
      typeof children === "function" ? children(state) : children;
    return (
      <Link
        ref={ref}
        to={to}
        className={cn(resolvedClass)}
        style={resolvedStyle}
        aria-current={isActive ? "page" : undefined}
        {...rest}
      >
        {resolvedChildren}
      </Link>
    );
  },
);

export function Navigate({
  to,
  replace,
}: {
  to: string;
  replace?: boolean;
  state?: unknown;
}) {
  return <TSNavigate to={to as never} replace={replace} />;
}

export function useLocation() {
  const loc = tsUseLocation();
  const search =
    typeof loc.searchStr === "string" && loc.searchStr.length > 0
      ? loc.searchStr.startsWith("?")
        ? loc.searchStr
        : `?${loc.searchStr}`
      : "";
  return {
    pathname: loc.pathname,
    search,
    hash: loc.hash ?? "",
    state: (loc as { state?: unknown }).state ?? null,
    key: (loc.href ?? loc.pathname) as string,
  };
}

type NavigateOptions = {
  replace?: boolean;
  state?: unknown;
  preventScrollReset?: boolean;
};

type NavigateFn = ((to: string | number, options?: NavigateOptions) => void) & {
  (options: { to?: string; replace?: boolean }): void;
};

export function useNavigate(): NavigateFn {
  const nav = tsUseNavigate();
  const fn = (arg: string | number | { to?: string; replace?: boolean }, options?: NavigateOptions) => {
    if (typeof arg === "number") {
      if (typeof window !== "undefined") window.history.go(arg);
      return;
    }
    if (typeof arg === "string") {
      nav({ to: arg as never, replace: options?.replace });
      return;
    }
    if (arg && typeof arg === "object") {
      nav({ to: (arg.to ?? ".") as never, replace: arg.replace });
    }
  };
  return fn as NavigateFn;
}

export function useParams<
  T extends Record<string, string | undefined> = Record<string, string | undefined>,
>(): T {
  return (tsUseParams as unknown as (opts: { strict: false }) => T)({ strict: false });
}

// No-Op-Router-Wrapper – TanStack liefert eigenen Router-Kontext im __root.
export function BrowserRouter({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
export function MemoryRouter({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
export const Routes: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <>{children}</>
);
export const Route: React.FC<AnyProps> = () => null;

export const Outlet: React.FC = () => null;
